<template>
  <div class="home-page">
    <div class="hero-section">
      <div class="hero-background">
        <div class="hero-video">
          <iframe 
            src="https://www.youtube.com/embed/Xq021QczKpY?autoplay=1&mute=1&loop=1&playlist=Xq021QczKpY&controls=0&showinfo=0&rel=0&modestbranding=1" 
            frameborder="0" 
            allow="autoplay; encrypted-media" 
            allowfullscreen>
          </iframe>
        </div>
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <h1 class="welcome-title" ref="title">The Circle - Private Estate</h1>
        <p class="welcome-subtitle" ref="subtitle">A Vision Rooted in Malawi, Inspired by the World</p>
        <div class="cta-buttons" ref="buttons">
          <a href="#" @click.prevent="scrollToSection('section1')" class="cta-btn primary">Explore Vision</a>
          <router-link to="/activities" class="cta-btn secondary">View Activities</router-link>
          <a href="https://wa.me/27729006987" target="_blank" class="cta-btn secondary">Get in Touch</a>
        </div>
      </div>
      <div class="scroll-indicator" ref="scrollIndicator">
        <div class="scroll-arrow"></div>
      </div>
    </div>
    
    <div class="content-sections">
      <section class="feature-section" ref="section1">
        <div class="feature-image">
          <img src="@/assets/IMG_2388.jpg" alt="The Circle Cultural Space" class="section-image" />
        </div>
        <div class="feature-content">
          <h2>Vision</h2>
          <p>The Circle is more than a hotel or a venue. It is a vision: a place where art, culture, learning, and community meet. A sanctuary for creativity and innovation, built in Malawi but with a global heart, while reaching 5-star standards.</p>
          
          <h3>The Story</h3>
          <p>I was born in Malawi, and life later took me across the world. In my 18-year career as a Systems Architect and CTO, I have worked in the software industry building global solutions, while traveling to over 50 countries. Every time I returned home, I felt the warmth of Malawi's people, yet I was also struck by what was missing.</p>
          
          <p>Malawi, with all its beauty and kindness, has not yet delivered the level of originality and excellence I have seen elsewhere, especially in hospitality. What the world offers in storytelling, culture, and unique experiences is too often absent here.</p>
          
          <p>I chose not to remain another voice of complaint. Instead, I became a voice of action. I took every cent from my savings and went ahead, purchasing land, beginning construction, and laying the foundation of what will become The Circle.</p>
          
          <p>This was not an easy decision. It was a leap of faith, driven by the belief that change is possible when we act. The Circle is my response: a place where global standards meet local identity, where Malawians can experience the world without leaving home, and where visitors can discover the depth of Malawi's untold story.</p>
          
          <p><strong>This is not just a seed for change, it is the foundation for transformation.</strong></p>
        </div>
      </section>
      
      <section class="feature-section reverse" ref="section2">
        <div class="feature-content">
          <h2>The Circle Today – Progress So Far</h2>
          <p>What began as a dream has already taken physical shape. With personal savings and determination, The Circle has moved beyond an idea and into reality.</p>
          
          <h3>Land and Construction</h3>
          <p>The Circle estate has been secured and fenced. Foundational work has begun on key structures, including the amphitheatre, accommodation pods, spa and pool area, and supporting facilities. Each phase is designed not only for luxury but also for community access and sustainability.</p>
          
          <h3>Events and Cultural Space</h3>
          <p>The amphitheatre is set to become the beating heart of The Circle, a stage for film nights, music, poetry, and cultural showcases. Beyond this, The Circle has an ambitious cultural program:</p>
          <ul>
            <li>Hosting an annual Music and Arts Awards Ceremony, celebrating Malawian talent and positioning the country on the regional and global cultural map.</li>
            <li>Building a Hall of Fame for Malawian artists, preserving their legacy and inspiring future generations.</li>
            <li>Every two years, the amphitheatre stage will be renamed after a Malawian artist, who will symbolically pass on the torch to the next through a dedicated committee.</li>
          </ul>
          
          <h3>Jobs Created</h3>
          <p>Since the project kicked off in March 2025, with planning beginning in September 2024, The Circle has already created more than 100 jobs in just seven months. These opportunities have supported builders, artisans, suppliers, and service providers. As we continue, our goal is to transition into long-term roles in hospitality, event management, landscaping, and administration, creating sustainable employment that transforms lives.</p>
          
          <h3>Community Engagement and Programs</h3>
          <p>The Circle is committed to uplifting the community from the very beginning.</p>
          <ul>
            <li>We have assisted Ligowe Primary School in building a kitchen that now feeds over 1,000 children a day through Mary's Meals.</li>
            <li>We have also adopted two other schools in the area, expanding our reach to more children and families.</li>
            <li>From the profits of The Circle, a significant percentage will go directly into education programs, starting with ensuring that all children of employees are supported with tuition and educational needs, creating a cycle where the growth of The Circle translates directly into the growth of families.</li>
            <li>We have initiated internships, giving young people exposure to real projects and professional growth.</li>
            <li>Workers on site are not just earning income, but also learning new ways of managing assets, collaborating, and growing personally in a structured workspace.</li>
          </ul>
          <p>The Circle has proven that even with limited resources, progress can be made. The vision is alive and what we need now is support to accelerate construction, scale community programs, and unlock the full potential of this space.</p>
        </div>
        <div class="feature-image">
          <img src="@/assets/IMG_2898.jpg" alt="The Circle Progress" class="section-image" />
        </div>
      </section>
      
      <section class="feature-section" ref="section3">
        <div class="feature-image">
          <img src="@/assets/IMG_3040.jpg" alt="The Circle Location" class="section-image" />
        </div>
        <div class="feature-content">
          <h2>Where is The Circle?</h2>
          <p>The Circle is rooted in a truly special place. We have secured seven hectares of land in Bvumbwe, an area rich in history, culture, and natural beauty.</p>
          
          <h3>Land Use and Growth Potential</h3>
          <p>About 40 percent of the land is already in use for structures such as the amphitheatre, pods, and supporting facilities, while 60 percent remains available for future development.</p>
          
          <h3>The Setting</h3>
          <p>Bvumbwe is a semi-residential area surrounded by tea farms, bluegum trees, and pine forests. Once home to one of Malawi's largest chicken factories, the area is now transforming into a vibrant residential hub and countryside retreat.</p>
          
          <h3>Accessibility</h3>
          <p>The Circle sits just 25 minutes from Blantyre city center, making it an easy escape for both locals and visitors while still remaining close to Malawi's commercial heartbeat.</p>
          
          <h3>Natural Beauty and Experiences</h3>
          <p>The land itself is blessed with valleys, rocky scenery, and mountain backdrops. A river cuts across the property, creating a serene environment where guests can retreat into nature while still enjoying modern comforts. The surrounding environment also offers:</p>
          <ul>
            <li>Biking and hiking trails, perfect for outdoor enthusiasts.</li>
            <li>Walks through the long, lush tea farms, immersing guests in Malawi's iconic agricultural landscapes.</li>
            <li>Authentic Malawian village experiences give visitors a chance to connect with the local culture and way of life.</li>
          </ul>
          <p>The Circle's location is more than geography; it is part of its identity. It blends Malawi's agricultural heritage, natural beauty, and emerging modern lifestyle into one space, offering visitors both connection and escape.</p>
        </div>
      </section>
      
      <section class="feature-section reverse" ref="section4">
        <div class="feature-content">
          <h2>The Design of The Circle</h2>
          <p>One of the most significant features of The Circle is its circular architecture. The rondavels you will see across the estate are not a stylistic accident; they are deeply intentional, rooted in both memory and culture.</p>
          
          <p>When I was young and visited my grandmother in Zomba, she lived within a collection of rondavels. Each served a different purpose: one as a kitchen, one as a living room and bedroom, another as a bathroom, and others as storage. Though separate in function, they were fenced into one space. To move between them, you had to step outside, catching a breeze, taking in a view, sensing the distinct smells of the land. It was a life connected to nature.</p>
          
          <p>This experience reflected a broader rhythm of African life in circles. In Malawian culture:</p>
          <ul>
            <li>Families gather in circles to eat.</li>
            <li>Communities sit in circles to discuss matters and resolve conflicts.</li>
            <li>People cook together in circles, live in circular homes, and form circles of trust and belonging.</li>
          </ul>
          
          <p>Circles are more than geometry; they are symbols of community, continuity, and balance with nature.</p>
          
          <p>This is why The Circle estate is designed as it is. The circular buildings create uniformity with deeper meaning, offering harmony instead of clinical lines and finishes. Every path, every space is designed to remind visitors that life is richer when lived in community, in connection, and in flow.</p>
        </div>
        <div class="feature-image">
          <img src="@/assets/progress/early_june.png" alt="The Circle Design" class="section-image" />
        </div>
      </section>
      
      <section class="feature-section" ref="section5">
        <div class="feature-image">
          <img src="@/assets/progress/workers.png" alt="The Circle Impact" class="section-image" />
        </div>
        <div class="feature-content">
          <h2>Why The Circle Matters – Impact</h2>
          <p>The Circle is more than buildings and land. It is a movement, a catalyst for transformation that touches lives in Malawi and resonates far beyond.</p>
          
          <h3>Community Impact</h3>
          <p>The Circle has already created more than 100 jobs, launched internships, supported schools, and built a kitchen that feeds over 1,000 children daily. Its future commitment is even stronger: profits will directly fund the education of employees' children, ensuring that families grow alongside the estate.</p>
          
          <h3>Cultural Impact</h3>
          <p>From the annual Music and Arts Awards Ceremony to the Hall of Fame for Malawian artists, The Circle will be a stage for expression and preservation of culture. Its amphitheatre will symbolically pass on the torch of creativity every two years, honoring artists while inspiring new ones.</p>
          
          <h3>Economic Impact</h3>
          <p>Positioned just 25 minutes from Blantyre, The Circle will stimulate tourism, weddings, conferences, and retreats. It will create a vibrant events economy and foster partnerships with local farmers, artisans, and suppliers, ensuring shared prosperity.</p>
          
          <h3>Global Impact</h3>
          <p>The Circle demonstrates how global standards and local identity can coexist. Through its very construction, it has uncovered innovations rarely seen elsewhere: nature guided safely out of building spaces, resources reused before permanent structures rise, and builders cultivating crops on the same land they work, eating from it within weeks. These practices reflect a deep connection to the land and model sustainable development that the world can learn from.</p>
        </div>
      </section>
      
      <!-- <section class="feature-section reverse highlight-section" ref="section6">
        <div class="feature-content">
          <h2>The Funding Need</h2>
          <p>The Circle has been built on personal sacrifice and vision. Every step so far has been self-funded. I invested every cent of my savings to prove that this vision is possible. Today, The Circle is alive and growing, but we have reached a ceiling where outside support is essential to continue.</p>
          
          <p><strong>The Circle is currently €250,000 away from completion.</strong> With this support, the goal can be achieved, allowing us to finish construction, open our doors, and deliver on the promise of jobs, education, and cultural transformation.</p>
          
          <p>At this stage, we are seeking partners and supporters who believe in vision-driven development. Your contribution will directly:</p>
          <ul>
            <li>Expand and complete core facilities, including the amphitheatre, pods, spa, pool, and cultural spaces.</li>
            <li>Create hundreds of sustainable jobs for Malawians.</li>
            <li>Fund education initiatives for the children and adopted schools.</li>
            <li>Build a cultural hub that celebrates Malawi's talent and inspires generations.</li>
          </ul>
          
          <h3>We welcome support in many forms:</h3>
          <ul>
            <li><strong>Individual Contributions and Donations:</strong> Every contribution, large or small, directly impacts the vision.</li>
            <li><strong>Business Sponsorships and Partnerships:</strong> Opportunities for branding, event partnerships, and shared growth with local and international companies.</li>
            <li><strong>Grants from Institutions and Foundations:</strong> Aligning with education, culture, community development, and sustainability goals.</li>
            <li><strong>Impact Investors:</strong> Partners who want measurable social returns alongside long-term financial sustainability.</li>
          </ul>
          
          <p>This is not just about building structures. It is about unlocking opportunities for thousands of Malawians while creating a cultural and economic model the world can learn from.</p>
        </div>
        <div class="feature-image">
          <img src="@/assets/progress/watersearch.jpg" alt="The Circle Funding" class="section-image" />
        </div>
      </section> -->
      
      <section class="feature-section" ref="section7">
        <div class="feature-image">
          <img src="@/assets/resto.jpg" alt="The Circle Timeline" class="section-image" />
        </div>
        <div class="feature-content">
          <h2>Timeline and Next Steps</h2>
          <p>The Circle has moved from vision to reality in under a year, and we are committed to maintaining this momentum.</p>
          
          <ul>
            <li><strong>December 2025:</strong> Completion of core structures on the estate, to get the venue ready for business. Once operational, the circle is trusted to take care of the remaining developments.</li>
            <li><strong>Q1 2026:</strong> Market readiness with the official opening for events, bookings, and cultural programs.</li>
            <li><strong>2026 onwards:</strong> Expansion of community programs, annual Music and Arts Awards, and development of additional facilities on the remaining land.</li>
          </ul>
          
          <p>This timeline ensures that The Circle is not a distant dream but an imminent reality. With support, we will open the doors within months, not years.</p>
        </div>
      </section>
      
      <!-- <section class="feature-section reverse call-to-action" ref="section8">
        <div class="feature-content">
          <h2>Call to Action</h2>
          <p>The Circle is not mine alone; it belongs to the community and to the world. It is a living vision built on sacrifice, culture, and hope. Already, it has created jobs, supported schools, and begun shaping a new story for Malawi. But this is only the beginning.</p>
          
          <p>By supporting The Circle, you are helping to build something lasting in Malawi:</p>
          <ul>
            <li>A place where art, culture, and community thrive.</li>
            <li>A sanctuary that connects people to nature, heritage, and innovation.</li>
            <li>A model for sustainable development that the world can learn from.</li>
          </ul>
          
          <p><strong>We invite you to join us as a supporter, a partner, or an investor in bringing this vision to its fullest life.</strong></p>
          <p><strong>Be part of The Circle. Together, we can create change that echoes for generations.</strong></p>
          
          <div class="action-buttons">
            <a href="https://wa.me/27729006987" target="_blank" class="cta-btn primary">Donate Now</a>
            <a href="https://wa.me/27729006987" target="_blank" class="cta-btn secondary">WhatsApp Me</a>
            <a href="https://wa.me/27729006987" target="_blank" class="cta-btn secondary">Partner With Us</a>
            <a href="https://maps.app.goo.gl/wS3BHpAbV1u4vB7Z6" target="_blank" class="cta-btn secondary">Google Maps</a>
          </div>
        </div>
        <div class="feature-image">
          <img src="@/assets/logo.jpeg" alt="The Circle Call to Action" class="section-image" />
        </div>
      </section> -->
      
      <section class="feature-section contact-section" ref="section9">
        <div class="feature-image">
          <img src="@/assets/logo.jpeg" alt="The Circle Contact" class="section-image" />
        </div>
        <div class="feature-content">
          <h2>Contact</h2>
          <h3>Locations and Numbers</h3>
          <div class="contact-info">
            <div class="contact-item">
              <span class="label">South Africa:</span>
              <span class="value">+27 72 900 6987 (WhatsApp)</span>
            </div>
            
            <div class="contact-item">
              <span class="label">Malawi:</span>
              <span class="value">+265 89 320 1114</span>
            </div>
            
            <div class="contact-item">
              <span class="label">Netherlands:</span>
              <span class="value">+31 68 47 555 21</span>
            </div>
            
            <div class="contact-item">
              <span class="label">Website:</span>
              <span class="value">www.scripta55.com</span>
            </div>
            
            <div class="contact-item">
              <span class="label">Email:</span>
              <span class="value">peter@tiye.services | peterphillip99@gmail.com</span>
            </div>
          </div>
        </div>
    </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  mounted() {
    this.initScrollAnimations();
    this.initParallax();
  },
  methods: {
    initScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, observerOptions);

      // Observe all sections
      const sections = this.$refs.section1 ? [
        this.$refs.section1,
        this.$refs.section2,
        this.$refs.section3,
        this.$refs.section4,
        this.$refs.section5,
        this.$refs.section6,
        this.$refs.section7,
        this.$refs.section8,
        this.$refs.section9
      ] : [];
      
      sections.forEach(section => {
        if (section) observer.observe(section);
      });

      // Initial hero animation
      setTimeout(() => {
        if (this.$refs.title) this.$refs.title.classList.add('animate-in');
        setTimeout(() => {
          if (this.$refs.subtitle) this.$refs.subtitle.classList.add('animate-in');
          setTimeout(() => {
            if (this.$refs.buttons) this.$refs.buttons.classList.add('animate-in');
            if (this.$refs.scrollIndicator) this.$refs.scrollIndicator.classList.add('animate-in');
          }, 300);
        }, 300);
      }, 100);
    },
    
    initParallax() {
      let ticking = false;
      const parallax = document.querySelector('.hero-section');
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          const scrolled = window.pageYOffset || 0;
          if (parallax) {
            const speed = Math.min(scrolled * 0.25, 200); // gentler parallax, clamp for mobile
            parallax.style.transform = `translate3d(0, ${speed}px, 0)`;
          }
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    },
    scrollToSection(sectionRef) {
      const element = this.$refs[sectionRef];
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home-page {
  min-height: 100vh;
  background: #fff;
  overflow-x: hidden;
}

.hero-section {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-video {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.hero-video iframe {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100vh;
  min-width: 177.77vh; /* 16:9 aspect ratio */
  transform: translate(-50%, -50%);
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.1) 100%),
    radial-gradient(circle at 50% 50%, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%);
  z-index: 2;
}

@keyframes grain {
  0%, 100% { transform: translate(0, 0); }
  10% { transform: translate(-2%, -2%); }
  20% { transform: translate(-4%, 2%); }
  30% { transform: translate(2%, -4%); }
  40% { transform: translate(-2%, 4%); }
  50% { transform: translate(-4%, 2%); }
  60% { transform: translate(4%, 0%); }
  70% { transform: translate(0%, 4%); }
  80% { transform: translate(2%, 8%); }
  90% { transform: translate(-2%, 2%); }
}

.hero-content {
  text-align: center;
  z-index: 3;
  position: relative;
  max-width: 800px;
  padding: 0 40px;
}

.welcome-title {
  font-family: 'Inter', sans-serif;
  font-size: 6rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  letter-spacing: -3px;
  opacity: 0;
  transform: translateY(50px);
  transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms ease;
  line-height: 0.8;
  text-shadow: 0 6px 40px rgba(0, 0, 0, 0.6), 0 0 1px rgba(255,255,255,0.2);
}

.welcome-title.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.welcome-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  color: rgba(255,255,255,0.92);
  line-height: 1.4;
  margin-bottom: 60px;
  font-weight: 300;
  opacity: 0;
  transform: translateY(30px);
  transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1) 0.2s, opacity 700ms ease 0.2s;
  letter-spacing: 1px;
  text-shadow: 0 3px 18px rgba(0,0,0,0.55);
}

.welcome-subtitle.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(30px);
  transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1) 0.4s, opacity 700ms ease 0.4s;
}

.cta-buttons.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.cta-btn {
  padding: 16px 32px;
  border-radius: 0;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  border: 2px solid transparent;
  letter-spacing: 1px;
  text-transform: uppercase;
  overflow: hidden;
}

.cta-btn.primary {
  background: #111;
  color: #fff;
  border-color: #111;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.cta-btn.primary:hover {
  background: #333;
  color: #fff;
  transform: translateY(-3px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.cta-btn.secondary {
  background: transparent;
  color: #111;
  border-color: #111;
}

.cta-btn.secondary:hover {
  background: #111;
  color: #fff;
  transform: translateY(-3px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

/* Accessibility: focus-visible for keyboard users */
.cta-btn:focus-visible {
  outline: 3px solid #111;
  outline-offset: 3px;
}

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #C8B560;
  opacity: 0;
  transition: all 1.5s cubic-bezier(0.16, 1, 0.3, 1) 0.9s;
  z-index: 3;
}

.scroll-indicator.animate-in {
  opacity: 1;
}

.scroll-arrow {
  width: 2px;
  height: 50px;
  background: linear-gradient(to bottom, #C8B560, transparent);
  position: relative;
  animation: bounce 2s infinite;
}

.scroll-arrow::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: -4px;
  width: 10px;
  height: 10px;
  border-right: 2px solid #C8B560;
  border-bottom: 2px solid #C8B560;
  transform: rotate(45deg);
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

.content-sections {
  position: relative;
  z-index: 2;
  padding: 0;
}

.feature-section {
  display: flex;
  align-items: center;
  min-height: 100vh;
  padding: 0;
  position: relative;
  opacity: 0;
  transform: translateY(60px);
  will-change: transform, opacity;
  transition: transform 600ms cubic-bezier(0.22, 1, 0.36, 1), opacity 600ms ease;
}

.feature-section.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.feature-section:nth-child(odd) {
  background: #ffffff;
}

.feature-section:nth-child(even) {
  background: #f7f7f7;
}

.feature-section.reverse {
  flex-direction: row-reverse;
}

.feature-image {
  flex: 1;
  height: 100vh;
  position: relative;
  overflow: hidden;
}

.section-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
}

.section-image:hover {
  transform: scale(1.05);
}

.image-placeholder {
  width: 100%;
  height: 100%;
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.image-placeholder.cultural {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  position: relative;
}

.image-placeholder.cultural::before {
  content: '🏛️';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.image-placeholder.design {
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  position: relative;
}

.image-placeholder.design::before {
  content: '⭕';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.image-placeholder.impact {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  position: relative;
}

.image-placeholder.impact::before {
  content: '🤝';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.image-placeholder.luxury {
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  position: relative;
}

.image-placeholder.luxury::before {
  content: '🌍';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.image-placeholder.funding {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  position: relative;
}

.image-placeholder.funding::before {
  content: '💰';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.image-placeholder.timeline {
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  position: relative;
}

.image-placeholder.timeline::before {
  content: '⏰';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.image-placeholder.contact {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  position: relative;
}

.image-placeholder.contact::before {
  content: '📞';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8rem;
  opacity: 0.3;
}

.feature-content {
  flex: 1;
  padding: 80px 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  max-width: 900px;
}

.feature-content h2 {
  font-family: 'Inter', sans-serif;
  font-size: 3.5rem;
  color: #111;
  margin-bottom: 24px;
  font-weight: 500;
  letter-spacing: -2px;
  line-height: 1.1;
}

.feature-content p {
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  color: #333;
  line-height: 1.6;
  font-weight: 300;
  margin-bottom: 40px;
}

.feature-content h3 {
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  color: #222;
  margin: 24px 0 12px 0;
  font-weight: 600;
}

.feature-content ul {
  margin: 16px 0 24px 20px;
  padding: 0;
}

.feature-content li {
  color: #333;
  line-height: 1.6;
  margin-bottom: 8px;
}

.feature-stats {
  display: flex;
  gap: 40px;
  margin-top: 20px;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-number {
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  color: #111;
  font-weight: 600;
  line-height: 1;
}

.stat-label {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 8px;
}

.design-elements {
  display: flex;
  gap: 20px;
  margin-top: 30px;
}

.circle-element {
  width: 60px;
  height: 60px;
  border: 2px solid #C8B560;
  border-radius: 50%;
  position: relative;
  animation: pulse 2s infinite;
}

.circle-element:nth-child(2) {
  animation-delay: 0.5s;
}

.circle-element:nth-child(3) {
  animation-delay: 1s;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.impact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 30px;
}

.impact-item {
  text-align: center;
  padding: 20px;
  background: rgba(200, 181, 96, 0.05);
  border: 1px solid rgba(200, 181, 96, 0.2);
  transition: all 0.3s ease;
}

.impact-item:hover {
  background: rgba(200, 181, 96, 0.1);
  transform: translateY(-5px);
}

.impact-number {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 2.2rem;
  color: #111;
  font-weight: 600;
  line-height: 1;
}

.impact-label {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 8px;
}

.standards-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 30px;
}

.standard-item {
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: #444;
  padding: 12px 0;
  border-bottom: 1px solid rgba(200, 181, 96, 0.2);
  transition: all 0.3s ease;
}

.standard-item:hover {
  color: #C8B560;
  padding-left: 10px;
}

.highlight-section {
  background: linear-gradient(135deg, rgba(200, 181, 96, 0.1) 0%, rgba(200, 181, 96, 0.05) 100%);
  border-left: 4px solid #C8B560;
}

.call-to-action {
  background: linear-gradient(135deg, rgba(200, 181, 96, 0.08) 0%, rgba(200, 181, 96, 0.03) 100%);
  border: 1px solid rgba(200, 181, 96, 0.2);
}

.action-buttons {
  display: flex;
  gap: 16px;
  margin-top: 40px;
  flex-wrap: wrap;
  justify-content: center;
}

.contact-section {
  background: #ffffff;
}

.contact-info {
  margin: 24px 0;
}

.contact-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(200, 181, 96, 0.1);
}

.contact-item .label {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: #111;
  margin-right: 12px;
  min-width: 100px;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.contact-item .value {
  font-family: 'Inter', sans-serif;
  color: #333;
  flex: 1;
  font-size: 1rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .feature-content {
    padding: 60px 40px;
  }
  
  .feature-content h2 {
    font-size: 2.8rem;
  }
  
  .impact-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .standards-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-content {
    padding: 0 20px;
  }
  
  .welcome-title {
    font-size: 4rem;
  }
  
  .welcome-subtitle {
    font-size: 1.2rem;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
  
  .cta-btn {
    width: 250px;
  }
  
  .feature-section {
    flex-direction: column;
    min-height: auto;
  }
  
  .feature-section.reverse {
    flex-direction: column;
  }
  
  .feature-image {
    height: 50vh;
  }
  
  .feature-content {
    padding: 60px 30px;
  }
  
  .feature-content h2 {
    font-size: 2.5rem;
  }
  
  .feature-stats {
    gap: 30px;
  }
  
  .design-elements {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .welcome-title {
    font-size: 3rem;
  }
  
  .welcome-subtitle {
    font-size: 1rem;
  }
  
  .feature-content h2 {
    font-size: 2rem;
  }
  
  .feature-content p {
    font-size: 1rem;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .impact-number {
    font-size: 1.8rem;
  }
}
</style>
